import * as R from 'ramda';
import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useState, useId, useRef } from 'react';
import useAsync from 'hooks/useAsync';
import { FormikHelpers } from 'formik';
import { sendSmsCode } from 'actions/sms';
import useCurrentUser from 'hooks/useCurrentUser';
import { toastError, toastSuccess } from 'components/Toast';
import { Button, Text, TextLink } from '@teachstonellc/component-library';
import {
  AlertCircle,
  CircleChecked
} from '@teachstonellc/component-library/dist/icons';
import RemovePhoneModal from './RemovePhoneModal';
import FocusFirstInvalidField from 'components/FocusFirstInvalidField';

import PhoneInput from 'components/PhoneInput';
import { Formik, Form, PreventNavigation } from 'components/EnhancedForm';

import {
  VerificationWrapper,
  VerificationStatus,
  InputWrapper,
  ButtonWrapper
} from './Styled';
import { updateUserMe } from 'actions/userMe';
import { useDispatch } from 'react-redux';
import { update } from 'store/slices/user';

const validationSchema = yup.object({
  phone_number: yup
    .string()
    .required(i18n.ft(messages.errors.phoneNumber.required))
});

interface MobileFormProps {
  /** If `true`, user is currently in the verification step. */
  isVerifying: boolean;

  /** Callback fired after the phone is updated and the sms code is sent. */
  onSuccess: () => void;

  /** Callback fired after the user decided to delete his phone number. */
  removedPhone: () => void;
}

function MobileForm({ isVerifying, onSuccess, removedPhone }: MobileFormProps) {
  const smsRequest = useAsync();
  const currentUser = useCurrentUser();
  const initialPhone = currentUser.login_phone || '';
  const [showAlert, setShowAlert] = useState(false);
  const userMeMutation = useAsync();
  const dispatch = useDispatch();
  const phoneInputId = useId();
  const phoneErrorId = useId();
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const [isInitialNotVerified] = useState(
    !R.isNil(currentUser.login_phone) &&
      R.isNil(currentUser.login_phone_confirmed_at)
  );

  function isVerified(phoneNumber: string) {
    const phoneWithExt =
      phoneNumber.charAt(0) === '+' ? phoneNumber : `+${phoneNumber}`;

    return (
      !R.isNil(currentUser.login_phone_confirmed_at) &&
      currentUser.login_phone === phoneWithExt
    );
  }

  function sendCode() {
    smsRequest.run(sendSmsCode(), {
      onSuccess: () => {
        onSuccess();
        toastSuccess({ description: i18n.ft(messages.success) });
      },
      onError: (error: any) => {
        toastError({ description: error.message });
      }
    });
  }

  function handleSubmit(values: any, formik: FormikHelpers<any>) {
    const params = { login_phone: values.phone_number };

    userMeMutation.run(updateUserMe(params), {
      onSuccess: (data: any) => {
        dispatch(update(data));
        formik.setSubmitting(false);
        sendCode();
      },
      onError: (error: any) => {
        const message =
          error.errors?.user?.login_phone?.[0] ??
          i18n.ft(messages.errors.phoneNumber.invalid);

        if (formik) {
          formik.setFieldError('phone_number', message);
          formik.setSubmitting(false);
        }
      }
    });
  }

  function removePhoneNumber() {
    userMeMutation.run(updateUserMe({ login_phone: null }), {
      onSuccess: (data: any) => {
        dispatch(update(data));
      }
    });

    if (!isInitialNotVerified) {
      removedPhone();
    }
  }

  function openAlert() {
    setShowAlert(true);
  }

  function closeModal() {
    setShowAlert(false);
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      initialValues={{ phone_number: initialPhone }}
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        errors,
        touched,
        submitCount,
        isSubmitting
      }) => (
        <Form noValidate>
          <FocusFirstInvalidField
            submitCount={submitCount}
            isPending={isSubmitting}
            fields={[
              {
                isInvalid: Boolean(touched.phone_number && errors.phone_number),
                inputRef: phoneInputRef
              }
            ]}
          />
          <PreventNavigation />
          <Text variant="p-sm" className="mb-2">
            {i18n.ft(messages.requiredFieldsNote)}
          </Text>
          <div className="mb-4">
            <InputWrapper>
              <label
                htmlFor={phoneInputId}
                className="block text-sm font-semibold mb-1"
              >
                {i18n.ft(messages.fields.phoneNumber)}
                <span
                  aria-hidden="true"
                  className="inline-flex w-4 h-4 items-center justify-center text-ds-tomato-10"
                >
                  <i className="fa-solid fa-asterisk fa-2xs" />
                </span>
              </label>
              <PhoneInput
                inputRef={phoneInputRef}
                value={values.phone_number}
                onChange={(value: string | undefined) =>
                  setFieldValue('phone_number', value || '')
                }
                onBlur={() => setFieldTouched('phone_number', true)}
                defaultCountry="US"
                countryCallingCodeEditable={false}
                international
                numberInputProps={{
                  id: phoneInputId,
                  'aria-required': true,
                  'aria-describedby': phoneErrorId,
                  'aria-invalid': touched.phone_number && !!errors.phone_number
                }}
              />
              {touched.phone_number && errors.phone_number && (
                <p
                  id={phoneErrorId}
                  className="mt-2 text-ds-tiny text-ds-text-negative m-0"
                >
                  {errors.phone_number}
                </p>
              )}
            </InputWrapper>
          </div>

          {isVerified(values.phone_number) && (
            <VerificationWrapper>
              <span aria-hidden="true" className="flex text-ds-text-positive">
                <CircleChecked size={16} />
              </span>
              <VerificationStatus>
                {i18n.ft(messages.isVerified)}
              </VerificationStatus>
            </VerificationWrapper>
          )}

          {!isVerified(values.phone_number) && isInitialNotVerified && (
            <VerificationWrapper>
              <span aria-hidden="true" className="flex text-ds-text-negative">
                <AlertCircle size={16} />
              </span>
              <VerificationStatus>
                {i18n.ft(messages.notVerified)}
              </VerificationStatus>
            </VerificationWrapper>
          )}

          <ButtonWrapper>
            <Button
              type="submit"
              variant={isVerifying ? 'secondary' : 'primary'}
              shape="rounded"
              loading={initialPhone === '' && userMeMutation.isLoading}
              disabled={
                isVerified(values.phone_number) || userMeMutation.isLoading
              }
              startIcon={
                !isVerified(values.phone_number) && isVerifying
                  ? 'fa-solid fa-arrow-rotate-right'
                  : undefined
              }
            >
              {isVerified(values.phone_number)
                ? i18n.ft(messages.verified)
                : isVerifying
                  ? i18n.ft(messages.resendCode)
                  : i18n.ft(messages.sendCode)}
            </Button>
            {initialPhone !== null && initialPhone !== '' && (
              <TextLink
                as="button"
                type="button"
                onClick={openAlert}
                startIcon="fa-solid fa-trash"
              >
                {i18n.ft(messages.remove)}
              </TextLink>
            )}
            <RemovePhoneModal
              onSuccess={() => {
                setFieldTouched('phone_number', false);
                setFieldValue('phone_number', '');
                removePhoneNumber();
              }}
              open={showAlert}
              handleAlert={closeModal}
              isVerified={isVerified(values.phone_number)}
            />
          </ButtonWrapper>
        </Form>
      )}
    </Formik>
  );
}

export default MobileForm;
